interface SlackNotificationData {
  type: 'landing_page_visit' | 'image_generation' | 'checkout_access' | 'stripe_access' | 'order_placed' | 'configurator_entry'
  source: 'address' | 'upload' | 'webcam'
  address?: string
  illustration?: string
  frameStyle?: string
  frameSize?: 'S' | 'M' | 'L'
  orderAmount?: number
  additionalInfo?: string
  userAgent?: string
  referrer?: string
  urlParams?: string
  selectedMode?: 'home' | 'pet'
  imageUrl?: string
}

export const sendSlackNotification = async (data: SlackNotificationData): Promise<void> => {
  try {
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/slack-notification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
      },
      body: JSON.stringify(data)
    })

    if (!response.ok) {
      const errorData = await response.json()
      console.error('Failed to send Slack notification:', errorData)
    } else {
      console.log('Slack notification sent successfully:', data.type)
    }
  } catch (error) {
    console.error('Error sending Slack notification:', error)
    // Don't throw error to avoid breaking the main application flow
  }
}

// Helper functions for specific notification types
export const notifyImageGeneration = async (source: 'address' | 'upload' | 'webcam', address?: string, selectedMode?: 'home' | 'pet', imageUrl?: string) => {
  // Capture URL parameters for UTM tracking
  const urlParams = window.location.search;
  
  // Get mode from localStorage if not provided
  const mode = selectedMode || (() => {
    const saved = localStorage.getItem('selectedMode');
    return (saved === 'pet' || saved === 'home') ? saved : 'home';
  })();
  
  await sendSlackNotification({
    type: 'image_generation',
    source,
    address,
    imageUrl,
    urlParams: urlParams,
    selectedMode: mode
  })
}

export const notifyCheckoutAccess = async (
  source: 'address' | 'upload' | 'webcam',
  illustration: string,
  frameStyle: string,
  frameSize: 'S' | 'M' | 'L',
  address?: string
) => {
  // Capture URL parameters for UTM tracking
  const urlParams = window.location.search;
  
  await sendSlackNotification({
    type: 'checkout_access',
    source,
    address,
    illustration,
    frameStyle,
    frameSize,
    urlParams: urlParams
  })
}

export const notifyStripeAccess = async (
  source: 'address' | 'upload' | 'webcam',
  illustration: string,
  frameStyle: string,
  frameSize: 'S' | 'M' | 'L',
  orderAmount: number,
  address?: string
) => {
  // Capture URL parameters for UTM tracking
  const urlParams = window.location.search;
  
  await sendSlackNotification({
    type: 'stripe_access',
    source,
    address,
    illustration,
    frameStyle,
    frameSize,
    orderAmount,
    urlParams: urlParams
  })
}

export const notifyOrderPlaced = async (
  source: 'address' | 'upload' | 'webcam',
  illustration: string,
  frameStyle: string,
  frameSize: 'S' | 'M' | 'L',
  orderAmount: number,
  address?: string
) => {
  // Capture URL parameters for UTM tracking
  const urlParams = window.location.search;
  
  await sendSlackNotification({
    type: 'order_placed',
    source,
    address,
    illustration,
    frameStyle,
    frameSize,
    orderAmount,
    urlParams: urlParams
  })
}

// Prevent double notifications in development (React.StrictMode)
let lastLandingPageNotification = 0;
const NOTIFICATION_COOLDOWN = 5000; // 5 seconds

export const notifyLandingPageVisit = async (selectedMode?: 'home' | 'pet') => {
  const now = Date.now();
  
  // Prevent duplicate notifications within cooldown period
  if (now - lastLandingPageNotification < NOTIFICATION_COOLDOWN) {
    console.log('🚫 Landing page notification skipped (cooldown active)');
    return;
  }
  
  lastLandingPageNotification = now;
  
  // Capture URL parameters for UTM tracking
  const urlParams = window.location.search;
  
  // Get mode from localStorage if not provided
  const mode = selectedMode || (() => {
    const saved = localStorage.getItem('selectedMode');
    return (saved === 'pet' || saved === 'home') ? saved : 'home';
  })();
  
  await sendSlackNotification({
    type: 'landing_page_visit',
    source: 'address', // Required field, but not used for landing page
    userAgent: navigator.userAgent,
    referrer: document.referrer,
    urlParams: urlParams,
    selectedMode: mode
  })
}

// Prevent double notifications for configurator entry
let lastConfiguratorEntryNotification = 0;

export const notifyConfiguratorEntry = async (
  source: 'address' | 'upload' | 'webcam',
  watercolorImageUrl: string,
  address?: string,
  selectedMode?: 'home' | 'pet'
) => {
  const now = Date.now();
  
  // Prevent duplicate notifications within cooldown period
  if (now - lastConfiguratorEntryNotification < NOTIFICATION_COOLDOWN) {
    console.log('🚫 Configurator entry notification skipped (cooldown active)');
    return;
  }
  
  lastConfiguratorEntryNotification = now;
  
  // Capture URL parameters for UTM tracking
  const urlParams = window.location.search;
  
  // Get mode from localStorage if not provided
  const mode = selectedMode || (() => {
    const saved = localStorage.getItem('selectedMode');
    return (saved === 'pet' || saved === 'home') ? saved : 'home';
  })();
  
  await sendSlackNotification({
    type: 'configurator_entry',
    source,
    address,
    imageUrl: watercolorImageUrl,
    urlParams: urlParams,
    selectedMode: mode
  })
} 